import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Paper,
} from '@material-ui/core';
import ConnectivityCheckForm from '../../../components/kea/connectivity/ConnectivityCheckForm';
import { CatalogStatusWidget } from '../../catalog-sync';
import { pluginAppInIframe } from '../../../util/appUtils';
import DataSettingsEmbedded from './DataSettingsEmbedded';
import styles from './DataSettings.module.scss';
import { useShellAppContext } from '../../shell/context/useShellAppContext';
import { createRuntimeCrawlerSettingsApi } from '../../../app/runtime/runtimeCrawlerSettingsApi';

/**
 * Data Settings Tab - Crawler configuration
 * Settings for shop access, authentication, catalog connectivity
 */
const DataSettings = ({ shopId, onRegisterRef, onHasChanges }) => {
  const formRef = useRef();
  const isEmbedded = pluginAppInIframe();
  const { runtime, routeShopId } = useShellAppContext();
  const effectiveShopId = routeShopId || shopId;
  const crawlerApi = useMemo(
    () => createRuntimeCrawlerSettingsApi(runtime, effectiveShopId),
    [effectiveShopId, runtime],
  );

  useEffect(() => {
    if (onRegisterRef) {
      onRegisterRef({
        save: async () => {
          // Delegate save to ConnectivityCheckForm (non-embedded only)
          if (!isEmbedded && formRef.current?.save) {
            await formRef.current.save();
          }
        },
        reload: async () => {
          // Delegate reload to ConnectivityCheckForm (non-embedded only)
          if (!isEmbedded && formRef.current?.reload) {
            await formRef.current.reload();
          }
        },
      });
    }
  }, [onRegisterRef, isEmbedded]);

  // Embedded (bridge-only) path: ConnectivityCheckForm uses legacy WizardSlice
  // and wsClient transport — must not fire on the embedded production shell.
  // Task 10G: render the bridge-backed panel instead of the 10D placeholder.
  if (isEmbedded) {
    return <DataSettingsEmbedded />;
  }

  return (
    <Box className={styles.root}>
      <Grid container spacing={3}>
        {/* Left column: Crawler settings form */}
        <Grid item xs={12} md={8}>
          <Paper className={styles.section} elevation={0} data-testid="crawler-access-card">
            <div className={styles.accent} />
            <ConnectivityCheckForm
              ref={formRef}
              autoCheck={false}
              showTitle
              onHasChanges={onHasChanges}
              crawlerApi={crawlerApi}
            />
          </Paper>
        </Grid>

        {/* Right column: Catalog Status */}
        <Grid item xs={12} md={4}>
          <CatalogStatusWidget />
        </Grid>
      </Grid>
    </Box>
  );
};

DataSettings.propTypes = {
  shopId: PropTypes.string,
  onRegisterRef: PropTypes.func,
  onHasChanges: PropTypes.func,
};

DataSettings.defaultProps = {
  shopId: null,
  onRegisterRef: null,
  onHasChanges: null,
};

export default DataSettings;
